import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PageTitleService } from './page-title.service';
import { TranslateService } from '@ngx-translate/core';

interface TitleConfig {
  title: string;
  translationKey?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AutomaticTitleService {
  private readonly MODULE_TITLES: Record<string, TitleConfig> = {
    // Mystery Shopper Module - all routes starting with msm-checklist or general-dashboard
    'msm-checklist': {
      title: 'Mystery Shopper',
      translationKey: 'pageTitle.mysteryShopperModule',
    },
    'general-dashboard': {
      title: 'Mystery Shopper',
      translationKey: 'pageTitle.mysteryShopperModule',
    },
    'project-head-ui': {
      title: 'Mystery Shopper',
      translationKey: 'pageTitle.mysteryShopperModule',
    },

    // System Code Module
    'system-code': { title: 'System Code' },

    // User Management Module
    'user-management': { title: 'User Management' },
    roles: { title: 'User Management' },
    'user-role': { title: 'User Management' },
  };

  private readonly PAGE_TITLES: Record<string, TitleConfig> = {
    // Section Card Tracker - specific page titles
    'section/list': { title: 'Sections' },
    'section/add': { title: 'Add Section' },
    'section/edit/:id': { title: 'Edit Section' },
    'section/view/:id': { title: 'Section Details' },

    // Project
    'project/list': { title: 'Projects' },
    'project/add/:id': { title: 'Add Project' },
    'project/addinfo/:id': { title: 'Project Information' },
    'project/edit/:projectId': { title: 'Edit Project' },
    'project/view/:id': { title: 'Project Details' },
    'project/section-project/:sectionId': { title: 'Projects' },

    // Project Change Requests
    'project-cr/all': { title: 'All Change Requests' },
    'project-cr/:id/list': { title: 'Change Requests' },
    'project-cr/:id/add': { title: 'Add Change Request' },
    'project-cr/view/:crId': { title: 'View Change Request' },
    'project-cr/:id/edit/:crId': { title: 'Edit Change Request' },

    // Tasks
    'tasks/Board': { title: 'Tasks Board' },
    'tasks/Backlog': { title: 'Tasks Backlog' },

    // Milestone
    'milestone/list': { title: 'Milestones' },
    'milestone/list/:id': { title: 'Milestones' },
    'milestone/add': { title: 'Add Milestone' },
    'milestone/edit/:editId': { title: 'Edit Milestone' },
    'milestone/view/:id': { title: 'Milestone Details' },

    // Tags
    'tags/list': { title: 'Tags' },
    'tags/add': { title: 'Add Tag' },
    'tags/edit/:id': { title: 'Edit Tag' },

    // Section Chart
    'section-chart': { title: 'Section Chart' },

    // Dashboard
    dashboard: { title: 'Dashboard' },

    // Reports
    reports: { title: 'Reports' },

    // Login and auth pages
    login: { title: 'Login' },
    'forget-password': { title: 'Forgot Password' },
    modules: { title: 'Module Selection' },

    // Mystery Shopper specific pages (override module-level title with page-specific)

    // Mystery Shopper Landing
    'msm-checklist/mystery-shopper': {
      title: 'Mystery Shopper Landing',
      translationKey: 'pageTitle.mysteryShopperLanding',
    },

    // Action Plan Module
    'msm-checklist/action-plan/list': {
      title: 'Action Plans',
      translationKey: 'actionPlan.pageTitles.list',
    },
    'msm-checklist/action-plan/dashboard': {
      title: 'Action Plan Dashboard',
      translationKey: 'actionPlan.pageTitles.dashboard',
    },
    'msm-checklist/action-plan/schedule': {
      title: 'Schedule Missing Action Plan',
      translationKey: 'actionPlan.pageTitles.schedule',
    },
    'msm-checklist/action-plan/create/:id': {
      title: 'Create Action Plan',
      translationKey: 'actionPlan.pageTitles.create',
    },
    'msm-checklist/action-plan/master/:id': {
      title: 'Action Plan Master',
      translationKey: 'actionPlan.pageTitles.master',
    },
    'msm-checklist/action-plan/item-master': {
      title: 'Action Plan Items',
      translationKey: 'actionPlan.pageTitles.itemMaster',
    },
    'msm-checklist/action-plan/item-details': {
      title: 'Action Item Details',
      translationKey: 'actionPlan.pageTitles.itemDetails',
    },
    'msm-checklist/action-plan/items-list': {
      title: 'Action Items List',
      translationKey: 'actionPlan.pageTitles.itemsList',
    },

    // Schedule Module
    'msm-checklist/schedule/list': {
      title: 'Schedule List',
      translationKey: 'pageTitle.scheduleList',
    },
    'msm-checklist/schedule/reschedule-list': {
      title: 'Reschedule List',
      translationKey: 'pageTitle.rescheduleList',
    },

    // Checklist UI Module
    'msm-checklist/checklist-ui/list': {
      title: 'Checklists',
      translationKey: 'pageTitle.checklists',
    },
    'msm-checklist/checklist-ui/closed-checklists': {
      title: 'Closed Checklists',
      translationKey: 'pageTitle.closedChecklists',
    },
    'msm-checklist/checklist-ui/add': {
      title: 'Add Checklist',
      translationKey: 'pageTitle.addChecklist',
    },
    'msm-checklist/checklist-ui/edit/:id': {
      title: 'Edit Checklist',
      translationKey: 'pageTitle.editChecklist',
    },
    'msm-checklist/checklist-ui/preview/:id': {
      title: 'Checklist Preview',
      translationKey: 'pageTitle.checklistPreview',
    },
    'msm-checklist/checklist-ui/render/:id': {
      title: 'Checklist',
      translationKey: 'pageTitle.renderChecklist',
    },

    // Agent UI Module
    'msm-checklist/agent-ui/dashboard': {
      title: 'Agent Dashboard',
      translationKey: 'pageTitle.agentDashboard',
    },
    'msm-checklist/agent-ui/tasks/pending': {
      title: 'Pending Tasks',
      translationKey: 'pageTitle.pendingTasks',
    },
    'msm-checklist/agent-ui/tasks/submitted': {
      title: 'Submitted Tasks',
      translationKey: 'pageTitle.submittedTasks',
    },
    'msm-checklist/agent-ui/tasks/modifications': {
      title: 'Tasks Requiring Modifications',
      translationKey: 'pageTitle.tasksRequiringModifications',
    },
    'msm-checklist/agent-ui/tasks/drafts': {
      title: 'Draft Tasks',
      translationKey: 'pageTitle.draftTasks',
    },
    'msm-checklist/agent-ui/reschedule-list': {
      title: 'Reschedule Requests',
      translationKey: 'pageTitle.rescheduleRequests',
    },
    'msm-checklist/agent-ui/schedule': {
      title: 'Agent Schedule',
      translationKey: 'pageTitle.agentSchedule',
    },

    // Department Admin UI Module
    'msm-checklist/department-admin/checklist-review-list': {
      title: 'Review Checklist',
      translationKey: 'pageTitle.reviewChecklist',
    },
    'msm-checklist/department-admin/checklist-per-question': {
      title: 'Checklist Per Question',
      translationKey: 'pageTitle.checklistPerQuestion',
    },
    'msm-checklist/department-admin/visits-questions': {
      title: 'Visits Questions',
      translationKey: 'pageTitle.visitsQuestions',
    },

    // Project Admin UI Module
    'msm-checklist/project-admin-ui': {
      title: 'Project Admin Dashboard',
      translationKey: 'pageTitle.projectAdminDashboard',
    },
    'msm-checklist/project-admin-ui/tasks': {
      title: 'Project Admin Tasks',
      translationKey: 'pageTitle.projectAdminTasks',
    },
    'msm-checklist/project-admin-ui/all-tasks': {
      title: 'All Tasks',
      translationKey: 'pageTitle.allTasks',
    },

    // Configuration Module
    'msm-checklist/configuration/stakeholders': {
      title: 'Stakeholders Configuration',
      translationKey: 'pageTitle.stakeholdersConfiguration',
    },
    'msm-checklist/configuration/hierarchy-to-category': {
      title: 'Hierarchy to Category',
      translationKey: 'pageTitle.hierarchyToCategory',
    },
    'msm-checklist/configuration/email-templates': {
      title: 'Email Templates',
      translationKey: 'pageTitle.emailTemplates',
    },

    // General Dashboard Module
    'msm-checklist/general-dashboard/divisions': {
      title: 'Divisions Dashboard',
      translationKey: 'pageTitle.divisionsDashboard',
    },
    'msm-checklist/general-dashboard/assets/:parentId': {
      title: 'Assets Dashboard',
      translationKey: 'pageTitle.assetsDashboard',
    },
    'msm-checklist/general-dashboard/sub-building/:parentId': {
      title: 'Sub Building Dashboard',
      translationKey: 'pageTitle.subBuildingDashboard',
    },
    'msm-checklist/general-dashboard/locations/:parentId/:divisionId': {
      title: 'Locations Dashboard',
      translationKey: 'pageTitle.locationsDashboard',
    },
    'msm-checklist/general-dashboard/locations/:parentId/:divisionId/:categoryId':
      {
        title: 'Locations Dashboard',
        translationKey: 'pageTitle.locationsDashboard',
      },
    'msm-checklist/general-dashboard/hospitality/:parentId/:divisionId': {
      title: 'Hospitality Dashboard',
      translationKey: 'pageTitle.hospitalityDashboard',
    },
    'general-dashboard/divisions': {
      title: 'Divisions Dashboard',
      translationKey: 'pageTitle.divisionsDashboard',
    },
    'general-dashboard/assets/:parentId': {
      title: 'Assets Dashboard',
      translationKey: 'pageTitle.assetsDashboard',
    },
    'general-dashboard/sub-building/:parentId': {
      title: 'Sub Building Dashboard',
      translationKey: 'pageTitle.subBuildingDashboard',
    },
    'general-dashboard/locations/:parentId/:divisionId': {
      title: 'Locations Dashboard',
      translationKey: 'pageTitle.locationsDashboard',
    },
    'general-dashboard/locations/:parentId/:divisionId/:categoryId': {
      title: 'Locations Dashboard',
      translationKey: 'pageTitle.locationsDashboard',
    },
    'general-dashboard/hospitality/:parentId/:divisionId': {
      title: 'Hospitality Dashboard',
      translationKey: 'pageTitle.hospitalityDashboard',
    },

    // Project Head UI Module
    'project-head-ui/schedule-review-list': {
      title: 'Schedule Review',
      translationKey: 'pageTitle.scheduleReview',
    },
    'msm-checklist/project-head-ui/project-head-tasks': {
      title: 'Project Head Tasks',
      translationKey: 'pageTitle.projectHeadTasks',
    },
    'project-head-ui/review/:id': {
      title: 'Schedule Review Details',
      translationKey: 'pageTitle.scheduleReviewDetails',
    },

    // System Code specific pages
    'system-code/list': { title: 'System Codes' },
    'system-code/view/:id/:code/:name': { title: 'System Code Details' },

    // User Management specific pages
    'user-management/users/list': { title: 'Users' },
    'user-management/users/add': { title: 'Add User' },
    'user-management/users/edit/:editId': { title: 'Edit User' },
    'user-management/users/view/:id': { title: 'User Details' },
    'user-management/roles/list': { title: 'Roles' },
    'user-management/roles/add': { title: 'Add Role' },
    'user-management/roles/edit/:editId': { title: 'Edit Role' },
    'user-management/roles/view/:id': { title: 'Role Details' },
  };

  constructor(
    private router: Router,
    private pageTitleService: PageTitleService,
    private translateService: TranslateService
  ) {}

  /**
   * Initialize automatic title updates based on route navigation
   * Call this method once during app initialization (in AppComponent)
   */
  initialize(): void {
    // Set initial title
    this.updateTitleFromUrl(this.router.url);

    // Subscribe to route changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateTitleFromUrl(event.urlAfterRedirects || event.url);
      });

    // Re-set title when language changes
    this.translateService.onLangChange.subscribe(() => {
      this.updateTitleFromUrl(this.router.url);
    });
  }

  /**
   * Update the browser title based on the current URL
   */
  private updateTitleFromUrl(url: string): void {
    // Remove leading slash and query parameters
    const cleanUrl = url.replace(/^\//, '').split('?')[0].split('#')[0];

    // Try to find exact page match first (most specific)
    const pageTitle = this.findPageTitle(cleanUrl);
    if (pageTitle) {
      this.setTitle(pageTitle);
      return;
    }

    // Fall back to module-level title
    const moduleTitle = this.findModuleTitle(cleanUrl);
    if (moduleTitle) {
      this.setTitle(moduleTitle);
      return;
    }

    // Default title if no match found
    this.setTitle({ title: 'Checklist' });
  }

  /**
   * Find page-specific title by matching URL patterns
   */
  private findPageTitle(url: string): TitleConfig | null {
    // Try exact match first
    if (this.PAGE_TITLES[url]) {
      return this.PAGE_TITLES[url];
    }

    // Try pattern matching (for routes with parameters like /edit/:id)
    for (const pattern in this.PAGE_TITLES) {
      if (this.matchesPattern(url, pattern)) {
        return this.PAGE_TITLES[pattern];
      }
    }

    return null;
  }

  /**
   * Find module-level title by checking URL prefix
   */
  private findModuleTitle(url: string): TitleConfig | null {
    // Check each module path prefix
    for (const modulePath in this.MODULE_TITLES) {
      if (url.startsWith(modulePath)) {
        return this.MODULE_TITLES[modulePath];
      }
    }

    return null;
  }

  /**
   * Check if URL matches a pattern (handles routes with parameters)
   */
  private matchesPattern(url: string, pattern: string): boolean {
    const urlParts = url.split('/');
    const patternParts = pattern.split('/');

    if (urlParts.length !== patternParts.length) {
      return false;
    }

    return patternParts.every((part, index) => {
      // Pattern parts starting with ':' are parameters and match anything
      return part.startsWith(':') || part === urlParts[index];
    });
  }

  /**
   * Set the browser title using PageTitleService
   */
  private setTitle(config: TitleConfig): void {
    if (config.translationKey) {
      // Use translated title if translation key is provided
      this.pageTitleService.setTitleTranslated(config.translationKey);
    } else {
      // Use plain text title
      this.pageTitleService.setTitle(config.title);
    }
  }
}
